/**
 * useFormContainer Hook
 * Main hook for managing FormContainer state and lifecycle
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { FormContainerConfig } from '../types/index';
import {
  setupFormContainerServices,
  cleanupFormContainerServices,
} from '../stores/serviceRegistry';
import { useMasterDataStore } from '../stores/MasterDataStore';
import { useChildFormRegistryStore } from '../stores/ChildFormRegistryStore';
import { useEventManagerStore } from '../stores/EventManagerStore';
import { ValidationStrategyFactory } from '../strategies/ValidationStrategies';

export interface UseFormContainerReturn {
  activeIndex: number;
  isLoading: boolean;
  isSaving: boolean;
  errors: Record<string, any[]>;
  handleTabChange: (index: number) => Promise<void>;
  handleSave: () => Promise<void>;
  handleReset: () => void;
  getAllData: () => Record<string, unknown>;
}

/**
 * Main hook for FormContainer
 * Manages:
 * - Service setup/cleanup
 * - Active tab state
 * - Loading and saving states
 * - Validation errors
 */
export function useFormContainer(
  config: FormContainerConfig,
  initialData: Record<string, unknown> = {}
): UseFormContainerReturn {
  const childForms = config.childForms ?? [];
  const [activeIndex, setActiveIndex] = useState(
    config.layoutConfig?.defaultActiveIndex ?? 0
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, any[]>>({});

  // Access stores
  const masterData = useMasterDataStore();
  const childFormRegistry = useChildFormRegistryStore();
  const eventManager = useEventManagerStore();

  // Keep refs to config and initialData so event handlers always call the latest
  // version without needing to re-register listeners on every prop change.
  const configRef = useRef(config);
  configRef.current = config;

  const initialDataRef = useRef(initialData);
  initialDataRef.current = initialData;

  // Initialize services and register event listeners
  useEffect(() => {
    setIsLoading(true);

    // Setup all services with latest initialData
    setupFormContainerServices(initialDataRef.current, childForms);

    // Listen for validation errors — reads from configRef to avoid stale closures
    const onValidationError = (event: any) => {
      if (event.formID && event.errors) {
        setErrors((prev) => ({
          ...prev,
          [event.formID]: event.errors,
        }));
        configRef.current.onValidationError?.(event.formID, event.errors);
      }
    };

    // Listen for data changes
    const onDataChanged = (event: any) => {
      configRef.current.onDataChange?.(event.formID, event.data);
    };

    eventManager.on('validation:error', onValidationError);
    eventManager.on('data:changed', onDataChanged);

    setIsLoading(false);

    // Cleanup only the listeners we registered — don't call cleanupFormContainerServices()
    // as it clears ALL listeners and breaks other FormContainers on the same page
    return () => {
      eventManager.off('validation:error', onValidationError);
      eventManager.off('data:changed', onDataChanged);
    };
  }, [childForms.length]);

  // Handle tab/step change with validation
  const handleTabChange = useCallback(
    async (newIndex: number) => {
      // Create validation strategy
      const validationStrategy = ValidationStrategyFactory.createStrategy(
        configRef.current.validationMode
      );

      // Check if current form is valid before switching
      const currentFormData = useMasterDataStore.getState().getData();
      const canSwitch = await validationStrategy.canSwitchForm(currentFormData);

      if (!canSwitch) {
        // Validation failed, don't switch
        return;
      }

      // Call child form change callback
      const childForm = childForms[newIndex];
      if (childForm) {
        configRef.current.onChildFormChange?.(newIndex, childForm.formID);
      }

      setActiveIndex(newIndex);
    },
    [activeIndex, childForms]
  );

  // Handle save across all child forms
  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const validationStrategy = ValidationStrategyFactory.createStrategy(
        configRef.current.validationMode
      );

      // Validate all forms
      const allData = useMasterDataStore.getState().getData();
      const validationErrors = await validationStrategy.validateBeforeSave(
        allData
      );

      if (validationErrors && validationErrors.length > 0) {
        validationStrategy.onValidationError(validationErrors);
        return;
      }

      // Call onSubmit callback if provided
      if (configRef.current.onSubmit) {
        await configRef.current.onSubmit(allData);
      }

      // Emit save event
      useEventManagerStore.getState().emit('form:saved', { data: allData });
    } finally {
      setIsSaving(false);
    }
  }, []);

  // Handle reset to initial data
  const handleReset = useCallback(() => {
    useMasterDataStore.getState().reset();
    setErrors({});
    setActiveIndex(configRef.current.layoutConfig?.defaultActiveIndex ?? 0);
  }, []);

  // Get all current data
  const getAllData = useCallback(() => {
    return useMasterDataStore.getState().getData();
  }, []);

  return {
    activeIndex,
    isLoading,
    isSaving,
    errors,
    handleTabChange,
    handleSave,
    handleReset,
    getAllData,
  };
}
